import { OperationPart } from '@1po/1po-bff-fe-spec/generated/catalog/easy_basket/response/GetEasyBasketOperationDetailResponse';
import { ReferenceBrandType } from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/Reference';
import { ReferenceStock, Warehouse } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { DealerType } from '@1po/1po-bff-fe-spec/generated/user/response/ConnectAsUserProfileResponse';
import { ReferencePriceType, SORTING_MATRIX } from 'domains/references';
import { getData, hasData, isLoading, isNotFound, LOADING, NO_DATA, SearchData } from 'utils';
import { Country } from 'utils/i18n/Country';

export const getMainReferenceToDisplay = (
  mainReferenceNumber: string,
  supersessionReferences: string[] | undefined,
  prices: Map<string, NO_DATA | ReferencePriceType>,
  stocks: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>,
): string => {
  if (supersessionReferences === undefined) {
    return mainReferenceNumber;
  }
  const getStocksAvailableForStockType = (stockForReference: Map<string, ReferenceStock>, stockType: Warehouse) => {
    return [...stockForReference.values()].some((refStocksPerSeller) =>
      refStocksPerSeller?.warehouses?.some((w) => w.type === stockType && w.confirmedQuantity > 0),
    );
  };

  const findReferenceWithPriceAndStockType = (references: string[], stockType: Warehouse): string | undefined => {
    return references.find((referenceNumber) => {
      const price = getData(prices.get(referenceNumber));
      const stockForReference = stocks.get(referenceNumber)?.data;
      if (!stockForReference) {
        return false;
      }
      return price && getStocksAvailableForStockType(stockForReference, stockType);
    });
  };

  const findReferenceWithPriceAndStock = (references: string[]): string | undefined => {
    return references.find((ref) => {
      const price = getData(prices.get(ref));
      const stock = stocks.get(ref)?.data;
      return price && stock;
    });
  };

  const localStockOrderDisplay = [mainReferenceNumber, ...supersessionReferences];
  // ONEPORTAL-6484 - find reference in supersession chain with r1 stock
  const localStockReference = findReferenceWithPriceAndStockType(localStockOrderDisplay, 'LOCAL');
  if (localStockReference) {
    return localStockReference;
  }

  // ONEPORTAL-6484 - find reference in reversed supersession chain with r1 stock
  const centralStockOrderDisplay = localStockOrderDisplay.slice().reverse();
  const centralStockReference = findReferenceWithPriceAndStockType(centralStockOrderDisplay, 'CENTRAL');
  if (centralStockReference) {
    return centralStockReference;
  }
  // if nothing found, find first reference (starting from the oldest) with price and stock
  return findReferenceWithPriceAndStock(centralStockOrderDisplay) ?? mainReferenceNumber;
};

const RENAULT_AGE_THRESHOLD = 4;
const DACIA_AGE_THRESHOLD = 3;
const DEFAULT_MAX_AGE = 99;

interface BrandRef {
  referenceNumber: string;
  designation?: string;
  brand?: ReferenceBrandType;
}

export const mapOperationParts = (parts: OperationPart[]): BrandRef[] =>
  parts.map((p) => ({ referenceNumber: p.reference, brand: p.brand, designation: p.designation }));

//apply filter and sort acording to ONEPORTAL-1804
export const getFilteredAndSortedReferences = <T extends BrandRef>({
  references,
  dealerType = 'R2',
  vehicle,
  prices,
  stocks,
  country,
}: {
  references: T[];
  dealerType?: DealerType;
  vehicle?: VehicleDetail;
  prices: Map<string, NO_DATA | ReferencePriceType>;
  stocks: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>;
  country?: string;
}): T[] => {
  let refs = references;
  if (country === Country.RO.key) {
    refs = refs.filter((r) => r.brand !== 'VALUE_PLUS');
  }
  if (!vehicle) {
    return refs;
  }
  const ageThreshold = vehicle.vehicleBrand === 'RENAULT' ? RENAULT_AGE_THRESHOLD : DACIA_AGE_THRESHOLD;
  const vehicleAge = vehicle.dataHubVehicle?.vehicleAge ?? DEFAULT_MAX_AGE;
  const age = vehicleAge < ageThreshold ? 'NEW' : 'OLD';
  const dealerMap = SORTING_MATRIX.get(age);
  const brandArray = dealerMap?.get(dealerType);

  const refsWithPrice = refs.reduce((acc, ref) => (hasData(prices.get(ref.referenceNumber)) ? acc + 1 : acc), 0);

  const filteredReferences = refs.filter((ref) => {
    if (
      refsWithPrice <= 1 &&
      (ref.brand !== 'MOTRIO' ||
        stocks.get(ref.referenceNumber)?.searchStatus === LOADING ||
        isLoading(prices.get(ref.referenceNumber)))
    ) {
      return true;
    }
    const refStock = stocks.get(ref.referenceNumber);
    const refPrice = prices.get(ref.referenceNumber);
    return (isLoading(refStock?.searchStatus) || hasData(refStock?.searchStatus)) && hasData(refPrice);
  });

  if (!brandArray) {
    return filteredReferences;
  }

  const refNumbersWithoutPrices = filteredReferences
    .filter((ref) => isNotFound(prices.get(ref.referenceNumber)))
    .map((ref) => ref.referenceNumber);

  const brandToRefs = filteredReferences.reduce((acc, next) => {
    const brand = next.brand ?? 'OTHER';
    acc.set(brand, [...(acc.get(brand) ?? []), next]);
    return acc;
  }, new Map<ReferenceBrandType, T[]>());

  // ONEPORTAL-3376 - if we have at least one reference from given brand with price, hide all without prices
  const filteredMap = Array.from(brandToRefs.entries()).reduce((acc, [_b, brandRefs]) => {
    const withPrices = brandRefs.filter((r) => !refNumbersWithoutPrices.includes(r.referenceNumber));
    const toDisplay = withPrices.length > 0 ? withPrices : filteredReferences;
    toDisplay.forEach((r) => acc.set(r.referenceNumber, r));
    return acc;
  }, new Map<string, T>());

  return Array.from(filteredMap.values())
    .filter((ref) => ref.brand && (brandArray.includes(ref.brand) || ref.brand === 'OTHER'))
    .sort((a, b) => {
      const aIdx = brandArray.indexOf(a.brand === 'OTHER' ? 'CENTRAL' : a.brand ?? 'CENTRAL');
      const bIdx = brandArray.indexOf(b.brand === 'OTHER' ? 'CENTRAL' : b.brand ?? 'CENTRAL');
      return aIdx > -1 && aIdx < bIdx ? -1 : 1;
    });
};
